/**
 * DeleteTimeEntryModal — shared confirm dialog for deleting a time
 * entry. Used by the day calendar grid, the week calendar grid, and
 * the list-view entry row. Extracting it from those three sites
 * (where it lived as a near-duplicate `<DeleteConfirmModal>`) keeps
 * the visual treatment + the ticket-summary line in one place.
 *
 * Visual shape:
 *   - Warning glyph in a soft rose circle on the left of the header
 *   - Ticket summary line: kind glyph + shorthand chip + title, OR
 *     "Untagged time" when the entry isn't attached to a deliverable
 *   - Time range + duration pills (calendar / pill chrome — matches
 *     the rest of the timesheet surface)
 *   - "This action can't be undone" warning
 *   - Cancel (ghost) + Delete (red destructive)
 */

import { useTranslation } from 'react-i18next'

import type { TimeEntry } from '@/api/types-bridge'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/ToastProvider'
import { formatDuration, formatTimeRange } from '@/features/time/date-helpers'
import { useDeleteTimeEntry } from '@/features/time/use-time-entries'

interface DeleteTimeEntryModalProps {
  open: boolean
  entry: TimeEntry
  onClose: () => void
  /**
   * Fires after a successful delete, before `onClose`. A host that is
   * itself showing the entry (the edit modal) closes with it — otherwise
   * it would sit open over an entry that no longer exists.
   */
  onDeleted?: () => void
  /** Opened on top of another dialog — see `Modal.stacked`. */
  stacked?: boolean
}

export function DeleteTimeEntryModal({
  open,
  entry,
  onClose,
  onDeleted,
  stacked = false,
}: DeleteTimeEntryModalProps) {
  const { t, i18n } = useTranslation('time')
  const { t: tCommon } = useTranslation('common')
  const deleteEntry = useDeleteTimeEntry()
  const toast = useToast()

  const handleDelete = async () => {
    try {
      await deleteEntry.mutateAsync({ id: entry.id })
      toast.success(t('entryEdit.deletedToast'))
      onDeleted?.()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('entryEdit.deleteFailedToast'))
    }
  }

  const durationSec = entry.endedAt
    ? Math.max(
        0,
        Math.floor(
          (new Date(entry.endedAt).getTime() - new Date(entry.startedAt).getTime()) / 1000,
        ),
      )
    : 0
  const isRunning = !entry.endedAt
  const range = formatTimeRange(entry.startedAt, entry.endedAt, i18n.language)
  const durationLabel = isRunning
    ? t('entryModal.confirmDeleteRunning')
    : formatDuration(durationSec)

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={t('entryModal.confirmDeleteAria')}
      stacked={stacked}
    >
      <Modal.Header>
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-danger/10 text-danger"
          >
            <TrashIcon />
          </span>
          <span>{t('entryModal.confirmDeleteTitle')}</span>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-3">
          {/* Ticket summary — only when the entry is attached to a
            deliverable. Untagged entries surface a softer "Untagged
            time" subtitle so the user still gets context. */}
          {entry.deliverableTitle ? (
            <div className="flex items-start gap-2 rounded-md border border-border bg-bg-muted px-3 py-2">
              {entry.shorthand && (
                <span className="inline-flex flex-none items-center rounded-full bg-bg-elevated px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                  {entry.shorthand}
                </span>
              )}
              <span className="text-sm font-medium text-fg">{entry.deliverableTitle}</span>
            </div>
          ) : (
            <p className="text-sm text-fg-subtle">{t('entryModal.confirmDeleteUntagged')}</p>
          )}

          {/* Time range + duration as side-by-side pills. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2.5 py-1 text-xs font-medium text-fg">
              <ClockIcon />
              {range}
            </span>
            <span
              className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold tabular-nums ${
                isRunning
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                  : 'bg-bg-muted text-fg'
              }`}
            >
              {durationLabel}
            </span>
          </div>

          <p className="text-sm text-fg-muted">{t('entryModal.confirmDeleteUndo')}</p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={deleteEntry.isPending}>
          {tCommon('actions.cancel')}
        </Button>
        <Button
          onClick={() => void handleDelete()}
          disabled={deleteEntry.isPending}
          variant="danger"
        >
          {deleteEntry.isPending ? t('entryModal.deleting') : t('entryModal.delete')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function TrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}
